import { useDispatch, useSelector } from "react-redux";

import useClearAndCloseRowsSelection from "app/pages/sales-intelligence/hooks/useClearAndCloseRowsSelection";
import trackingService from "app/pages/sales-intelligence/services/tracking/createMultiSelectorPanelService";
import SaveToListPanel from "app/pages/sales-intelligence/sub-modules/opportunities/components/table-actions-panel/SaveToListPanel/SaveToListPanel";
import { useFetchOpportunityListsIfNotPresent } from "app/pages/sales-intelligence/sub-modules/opportunities/hooks/useFetchOpportunityListsIfNotPresent";
import useTrackedWebsitesQuota from "app/pages/sales-intelligence/sub-modules/opportunities/hooks/useTrackedWebsitesQuota";
import useUntrackedDomainsCount from "app/pages/sales-intelligence/sub-modules/opportunities/hooks/useUntrackedDomainsCount";
import {
    selectOpportunityListCreating,
    selectOpportunityListUpdating,
} from "app/pages/sales-intelligence/sub-modules/opportunities/store/selectors";
import type { OpportunityListType } from "app/pages/sales-intelligence/sub-modules/opportunities/types";
import type { BaseActivatedPanelProps } from "app/pages/sales-intelligence/sub-modules/table-actions-panel/components/BaseActivatedPanel/types";
import type { SelectionDropdownItem } from "app/pages/sales-intelligence/sub-modules/table-actions-panel/models/selection-dropdown-item/types";

import ManualSelectionItem from "../../../../table-actions-panel/models/selection-dropdown-item/ManualSelectionItem/ManualSelectionItem";
import { COMPETITIVE_LANDSCAPE_TABLE_KEY, COMPETITIVE_LANDSCAPE_TABLE_PROPERTY } from "../../configuration/constants";
import useCompetitiveQueryParams from "../../hooks/useCompetitiveQueryParams";
import saveToListSelectionDropdown from "../../models/save-to-list/saveToListSelectionDropdown";
import { tableRowsSelectionToggledAction } from "../../store/action-creators";
import { addDomainsToListThunk, addTopNDomainsToListThunk } from "../../store/effects";
import { selectCheckedWebsites, selectCompetitiveResultsCount, selectIsFetchingResults } from "../../store/selectors";

const SaveToListCompetitivePanelContainer = (props: BaseActivatedPanelProps) => {
    const dispatch = useDispatch();
    const { remaining } = useTrackedWebsitesQuota();
    const isListCreateInProgress = useSelector(selectOpportunityListCreating);
    const isListUpdateInProgress = useSelector(selectOpportunityListUpdating);
    const selectedWebsites = useSelector(selectCheckedWebsites);
    const numberOfUntrackedSelectedWebsites = useUntrackedDomainsCount(selectedWebsites);
    const numberOfResults = useSelector(selectCompetitiveResultsCount);
    const isFetchingResults = useSelector(selectIsFetchingResults);
    const { params } = useCompetitiveQueryParams();

    useFetchOpportunityListsIfNotPresent();

    const clearAndCloseRowsSelection = useClearAndCloseRowsSelection(
        tableRowsSelectionToggledAction,
        COMPETITIVE_LANDSCAPE_TABLE_KEY,
        COMPETITIVE_LANDSCAPE_TABLE_PROPERTY,
    );

    function handleDeactivate() {
        clearAndCloseRowsSelection();
        props.onDeactivate();
    }

    function handleSelectionChange(item: Readonly<SelectionDropdownItem>) {
        if (item.id === ManualSelectionItem.ID) {
            return dispatch(tableRowsSelectionToggledAction(true));
        }

        clearAndCloseRowsSelection();
    }

    async function handleSubmit(list: Readonly<OpportunityListType>, selectionItem: Readonly<SelectionDropdownItem>) {
        trackingService.trackMultiSelectorSaveToListClick(remaining, selectionItem);

        if (selectionItem.id === ManualSelectionItem.ID) {
            await dispatch(addDomainsToListThunk(list, selectedWebsites));
        } else {
            await dispatch(addTopNDomainsToListThunk(list, selectionItem.value, params));
        }

        return handleDeactivate();
    }

    return (
        <SaveToListPanel
            isLoading={isFetchingResults}
            isSubmitInProgress={isListCreateInProgress || isListUpdateInProgress}
            numberOfRemainingItems={remaining}
            numberOfResults={numberOfResults ?? 0}
            numberOfSelectedItems={selectedWebsites.length}
            numberOfUntrackedSelectedItems={numberOfUntrackedSelectedWebsites}
            selectionDropdown={saveToListSelectionDropdown}
            onDeactivate={handleDeactivate}
            onSelectionChange={handleSelectionChange}
            onSubmit={handleSubmit}
        />
    );
};

export default SaveToListCompetitivePanelContainer;
